import type { FrameSection } from './types';

/**
 * Desktop layout mirrors FOUNDER-DASHBOARD-000001 / DASHBOARD-ARCHITECTURE.
 * Column math: 12-col system; paired rows sum to 12.
 */
export const FRAME_SECTIONS: readonly FrameSection[] = [
  {
    id: 'daily-brief',
    title: 'Daily Brief',
    purpose: 'Implemented in FOUNDER-DASHBOARD-IMPLEMENTATION-000002 (live panel).',
    colSpan: 12,
    minHeight: 140,
  },
  {
    id: 'executive-kpis',
    title: 'Executive KPIs',
    purpose: 'Implemented in FOUNDER-DASHBOARD-IMPLEMENTATION-000003 (live strip).',
    colSpan: 12,
    minHeight: 120,
  },
  {
    id: 'overlay-graph',
    title: 'Overlay Graph',
    purpose: 'Implemented in FOUNDER-DASHBOARD-IMPLEMENTATION-000004 (live Master Overlay).',
    colSpan: 12,
    minHeight: 280,
  },
  {
    id: 'sales-24h',
    title: '24 Hour Sales',
    purpose: 'Implemented in FOUNDER-DASHBOARD-IMPLEMENTATION-000005 (live Sales Pulse).',
    colSpan: 6,
    minHeight: 240,
  },
  {
    id: 'india-map',
    title: 'India Map',
    purpose: 'Implemented in FOUNDER-DASHBOARD-IMPLEMENTATION-000007 (live India BI Map).',
    colSpan: 6,
    minHeight: 240,
  },
  {
    id: 'ops-inventory',
    title: 'Inventory Intelligence',
    purpose: 'Live sample: fulfillable, inbound, zero-stock from inventory page 1.',
    colSpan: 6,
    minHeight: 200,
    operational: true,
  },
  {
    id: 'ops-operations',
    title: 'Operational Overview',
    purpose: 'Implemented in FOUNDER-DASHBOARD-IMPLEMENTATION-000006 (Ops Intelligence Panel).',
    colSpan: 6,
    minHeight: 200,
    operational: true,
  },
  {
    id: 'ops-finance',
    title: 'Financial Overview',
    purpose: 'Live finance events page 1 when the domain returns rows.',
    colSpan: 6,
    minHeight: 200,
    operational: true,
  },
  {
    id: 'ops-traffic',
    title: 'Traffic Overview',
    purpose: 'Shows Sales & Traffic sessions when the report is stored.',
    colSpan: 6,
    minHeight: 200,
    operational: true,
  },
  {
    id: 'ops-health',
    title: 'Business Health',
    purpose: 'Same Business Health composite as Daily Brief.',
    colSpan: 6,
    minHeight: 180,
    operational: true,
  },
  {
    id: 'ops-returns',
    title: 'Returns',
    purpose: 'Amazon returns report sample when rows exist.',
    colSpan: 6,
    minHeight: 200,
    operational: true,
  },
  {
    id: 'ops-cod',
    title: 'COD vs Prepaid',
    purpose: 'Selected-period item-price split by cash on delivery vs prepaid.',
    colSpan: 6,
    minHeight: 200,
    operational: true,
  },
  {
    id: 'ops-swot',
    title: 'SWOT',
    purpose: 'Strengths and risks copied from ops/focus rules.',
    colSpan: 6,
    minHeight: 180,
    operational: true,
  },
  {
    id: 'ops-activity',
    title: 'Activity Feed',
    purpose: 'Last successful Amazon data pull per area.',
    colSpan: 12,
    minHeight: 180,
    operational: true,
  },
  {
    id: 'timeline',
    title: 'Timeline',
    purpose: 'Daily overlay points (GMV, order items, units).',
    colSpan: 12,
    minHeight: 200,
  },
  {
    id: 'executive-summary',
    title: 'Executive Summary',
    purpose: 'Narrative of yesterday and selected-period GMV already on canvas.',
    colSpan: 12,
    minHeight: 180,
  },
] as const;

/** Tailwind col-span classes for 12-column responsive grid. */
export function colSpanClass(span: FrameSection['colSpan']): string {
  switch (span) {
    case 3:
      return 'col-span-12 sm:col-span-6 lg:col-span-3';
    case 4:
      return 'col-span-12 sm:col-span-6 lg:col-span-4';
    case 6:
      return 'col-span-12 lg:col-span-6';
    case 12:
    default:
      return 'col-span-12';
  }
}

/** Pure check: paired 6+6 rows and full-width 12s are complete. */
export function validateFrameLayout(sections: readonly FrameSection[]): {
  ok: boolean;
  errors: string[];
} {
  const errors: string[] = [];
  let row = 0;
  for (const s of sections) {
    if (s.colSpan === 12) {
      if (row !== 0) {
        errors.push(`${s.id}: starts full-width while row has leftover ${row}`);
      }
      row = 0;
      continue;
    }
    row += s.colSpan;
    if (row > 12) {
      errors.push(`${s.id}: overflows 12-column row (sum ${row})`);
      row = s.colSpan;
    } else if (row === 12) {
      row = 0;
    }
  }
  if (row !== 0) {
    errors.push(`Final row incomplete (remainder ${row})`);
  }
  return { ok: errors.length === 0, errors };
}
